/**
 * Structure follows `customers/List.tsx` — `PagedTable` over a server-paged list, per this repo's
 * one table implementation (`components/table/*`). Same template source as `customers/columns.tsx`
 * (see that file's own header) — `roles-permissions/users-table`, this app's second screen off it.
 *
 * Search, status and role are the API's own `?q=&status=&role=` (`admin-users.md`), applied inside
 * the caller's scope server-side — so they search every user, not the page on screen. Status opens
 * on Active, like every other list here; `?status=all` is everyone.
 */
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { Button } from "rizzui";
import { EmptyState } from "@steelyard/theme";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { SearchInput } from "../../components/table/search-input";
import { userColumns } from "./columns";
import { Filters, USER_STATUSES } from "./filters";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";
import { listUsers } from "../../lib/services/identity";
import { listGrantableRoles } from "../../lib/identity";
import { hrefWith, readPaging } from "../../lib/paging";

const DEFAULT_STATUS = "active";

export async function usersListLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const { page, pageSize } = readPaging(url.searchParams);
  const q = url.searchParams.get("q") ?? undefined;
  const role = url.searchParams.get("role") ?? undefined;
  // Only a status this screen offers: the API answers 400 to any other, which would fail the page.
  const rawStatus = url.searchParams.get("status");
  const status =
    rawStatus === "all" ? undefined : USER_STATUSES.some((option) => option.value === rawStatus) ? rawStatus! : DEFAULT_STATUS;
  const [data, roles] = await Promise.all([
    listUsers({ page, pageSize, q, role, status }),
    // A failed roles read costs the role filter, not the list.
    listGrantableRoles().catch(() => [] as string[]),
  ]);
  return { data, roles, q, role, status };
}

export default function UsersList() {
  const { data, roles, q, role, status } = useLoaderData() as Awaited<ReturnType<typeof usersListLoader>>;
  const { account, permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const navigateTo = useNavigate();

  const query = (over: Record<string, string | undefined>) => hrefWith("/users", searchParams, over);
  // `filtered` is "differs from the default view" (Reset); `narrowed` is "hides any user".
  const filtered = Boolean(q || role || status !== DEFAULT_STATUS);
  const narrowed = Boolean(q || role || status);

  const statusOptions: StatusOption[] = [
    { label: "All users", href: query({ status: "all", page: undefined }), active: !status },
    ...USER_STATUSES.map((option) => ({
      label: option.label,
      href: query({ status: option.value, page: undefined }),
      active: option.value === status,
      status: option.key,
    })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Users"
        count={data.totalCount}
        description="Staff and company users in your scope — role, status and lockout."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Users" }]}
      />
      <PagedTable
        result={data}
        caption="Users, with their role, status and lockout state"
        columns={userColumns(can(permissionState, "user.status"), account.id, can(permissionState, "company.user.administer"))}
        getRowId={(row) => row.id}
        status={{ label: "Status", options: statusOptions }}
        search={
          <SearchInput value={q} placeholder="Search name, email or mobile…" onSearch={(next) => navigateTo(query({ q: next, page: undefined }))} />
        }
        filters={<Filters role={role} roles={roles} query={query} />}
        clear={{ active: filtered, href: query({ q: undefined, role: undefined, status: undefined, page: undefined }) }}
        empty={
          narrowed ? (
            <EmptyState
              title="No users matched"
              action={
                <Link to="/users?status=all">
                  <Button as="span" size="sm" variant="outline">
                    Show every user
                  </Button>
                </Link>
              }
            >
              Nothing matched those filters. Clear them to see every user in your scope.
            </EmptyState>
          ) : (
            <EmptyState title="No users">
              No account is in your scope yet. Staff users are created from an invitation; company users
              arrive through the portal&apos;s own registration.
            </EmptyState>
          )
        }
      />
      <p className="text-muted-foreground -mt-2 text-xs">
        A Company Admin sees only their own company&apos;s users — the list is scoped before it is fetched, not
        filtered after.
      </p>
    </div>
  );
}
